// Private Stations - shared specifications between client and server
// See docs/mechanics/private_stations.md for full design

export type PrivateStationTier = 1 | 2 | 3 | 4;
export type PrivateStationStatus = 'active' | 'suspended';

export interface PrivateStationTierSpec {
  tier: PrivateStationTier;
  droneCapacity: number;
  storageStacks: number;
  baseUpkeepPerDay: number;
  buyCost: number | null;          // T1 only; null for tiers reached via upgrade
  upgradeCostFromPrev: number | null; // null for T1
}

export const PRIVATE_STATION_TIERS: Record<PrivateStationTier, PrivateStationTierSpec> = {
  1: { tier: 1, droneCapacity: 5,  storageStacks: 50,  baseUpkeepPerDay: 2_000,  buyCost: 50_000, upgradeCostFromPrev: null },
  2: { tier: 2, droneCapacity: 10, storageStacks: 120, baseUpkeepPerDay: 5_000,  buyCost: null,   upgradeCostFromPrev: 200_000 },
  3: { tier: 3, droneCapacity: 15, storageStacks: 250, baseUpkeepPerDay: 12_000, buyCost: null,   upgradeCostFromPrev: 600_000 },
  4: { tier: 4, droneCapacity: 20, storageStacks: 500, baseUpkeepPerDay: 25_000, buyCost: null,   upgradeCostFromPrev: 1_500_000 },
};

// Per active drone, added to base upkeep
export const PRIVATE_STATION_UPKEEP_PER_DRONE: number = 500;

// Days a station may stay suspended before destruction
export const PRIVATE_STATION_DEFAULT_DAYS: number = 30;

// Daily upkeep tick cadence in real ms
export const PRIVATE_STATION_UPKEEP_INTERVAL_MS: number = 24 * 60 * 60 * 1000;

// Spatial placement constraints
export const PRIVATE_STATION_MIN_DISTANCE_FROM_STATION: number = 5_000;
export const PRIVATE_STATION_MIN_DISTANCE_FROM_ASTEROID: number = 2_000;
export const PRIVATE_STATION_MIN_DISTANCE_FROM_PRIVATE: number = 4_000;
// Constellation radius safe band — keep within (CONSTELLATION_HALF - margin)
export const PRIVATE_STATION_PLACEMENT_MARGIN: number = 20_000;
// Random placement retries before falling back
export const PRIVATE_STATION_PLACEMENT_MAX_TRIES: number = 32;

// Visual / collision (same scale as NPC stations)
export const PRIVATE_STATION_COLLISION_RADIUS: number = 400;
export const PRIVATE_STATION_SAFE_ZONE_RADIUS: number = 1_500;

export function dailyUpkeepFor(tier: PrivateStationTier, activeDrones: number): number {
  const spec = PRIVATE_STATION_TIERS[tier];
  return spec.baseUpkeepPerDay + activeDrones * PRIVATE_STATION_UPKEEP_PER_DRONE;
}

export function buyCostT1(): number {
  return PRIVATE_STATION_TIERS[1].buyCost ?? 0;
}

export function upgradeCost(fromTier: PrivateStationTier): number | null {
  if (fromTier >= 4) return null;
  const next = (fromTier + 1) as PrivateStationTier;
  return PRIVATE_STATION_TIERS[next].upgradeCostFromPrev;
}

export interface PrivateStationDTO {
  id: string;
  ownerType: 'player' | 'corp';
  ownerId: string;
  ownerName: string;            // player username, or corp "Name [TAG]"
  constellationId: string;
  name: string;
  tier: PrivateStationTier;
  posX: number;
  posY: number;
  status: PrivateStationStatus;
  suspendedSince: number | null;   // epoch ms
  lastUpkeepAt: number;            // epoch ms
}
